import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

interface StartingUniverseState {
  /** Map of rule ID to expanded state for eligibility rule sections */
  expandedEligibilityRuleIds: Record<string, boolean>;
  /** Map of filter section ID to expanded state */
  expandedFilterSectionIds: Record<string, boolean>;
  /**
   * Per-histogram open state. FilterSection unmounts its children when it
   * collapses, and a histogram's own `expanded` used to re-derive from whether
   * its value was still narrowed — so a filter the user had cleared came back
   * as an unconfigured "Set Range" button. Keeping the flag here survives that
   * remount.
   */
  expandedHistogramIds: Record<string, boolean>;
}

interface StartingUniverseActions {
  /** Toggle the expanded/collapsed state of an eligibility rule section */
  toggleEligibilityRule: (ruleId: string) => void;
  /** Toggle the expanded/collapsed state of a filter section */
  toggleFilterSection: (sectionId: string) => void;
  /** Initialize a filter section with a default open state (no-op if already set) */
  initFilterSection: (sectionId: string, defaultOpen: boolean) => void;
  /**
   * Return the step to how it looks on a fresh draft: expand/collapse state
   * cleared so the per-section defaults apply, and Primary Share Class back
   * OFF. Called when a draft enters the form, so nothing carries over from a
   * previous one in the same session.
   */
  resetFilterSections: () => void;
  setHistogramExpanded: (histogramId: string, expanded: boolean) => void;
}

export const useStartingUniverseStore = create<
  StartingUniverseState & StartingUniverseActions
>()(
  devtools(
    immer((set) => ({
      expandedEligibilityRuleIds: {} as Record<string, boolean>,
      expandedFilterSectionIds: {} as Record<string, boolean>,
      expandedHistogramIds: {} as Record<string, boolean>,

      toggleEligibilityRule: (ruleId) =>
        set((state) => {
          state.expandedEligibilityRuleIds[ruleId] =
            !state.expandedEligibilityRuleIds[ruleId];
        }),

      toggleFilterSection: (sectionId) =>
        set((state) => {
          state.expandedFilterSectionIds[sectionId] =
            !state.expandedFilterSectionIds[sectionId];
        }),

      initFilterSection: (sectionId, defaultOpen) =>
        set((state) => {
          if (!(sectionId in state.expandedFilterSectionIds)) {
            state.expandedFilterSectionIds[sectionId] = defaultOpen;
          }
        }),

      setHistogramExpanded: (histogramId, expanded) =>
        set((state) => {
          state.expandedHistogramIds[histogramId] = expanded;
        }),

      resetFilterSections: () =>
        set((state) => {
          state.expandedEligibilityRuleIds = {};
          state.expandedFilterSectionIds = {};
          state.expandedHistogramIds = {};
        }),
    })),
    { name: 'StartingUniverseStore' },
  ),
);
